import { create } from "zustand";
import { Order, OrderStatus, ActivityLog } from "@/types/orders";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  deductManualInventoryForOrder,
  addBackManualInventoryForOrder,
} from "@/services/inventory/manualInventoryService";

// Define the OrderStore interface

interface EmployeeMetrics {
  completedOrders: number;
  totalRevenue: number;
  employeeId: string;
}
export interface OrderStore {
  orders: Order[];
  selectedStoreId: string | null;
  setSelectedStoreId: (storeId: string | null) => void;
  fetchOrders: (storeId: string) => Promise<void>;
  updateOrderStatus: (orderId: string, status: OrderStatus) => Promise<void>;
  cancelOrder: (orderId: string, reason: string) => Promise<void>;
  subscribeToOrdersRealtime: (storeId: string) => void;
  unsubscribeFromOrdersRealtime: () => void;
  deleteOrder: (orderId: string) => Promise<void>;

  // Add methods referenced in components
  pendingOrdersCount: number;
  activityCount: number;
  updatePendingOrdersCount: () => void;
  markActivityViewed: (activityId: string) => Promise<void>;
  getTotalOrderAmount: () => number;
  isLoading: boolean;
  subscribeToActivitiesRealtime: (storeId: string) => void;
  unsubscribeFromActivitiesRealtime: () => void;
  addOrder: (order: Order) => void;
  initGlobalSubscription: () => void;

  // Add this to the OrderStore interface

  // Add these methods to the store
  getEmployeeMetrics: (employeeId: string) => EmployeeMetrics;
  getAllEmployeeMetrics: () => EmployeeMetrics[];
  updateOrder: (updatedOrder: Partial<Order>) => void;
}

const createOrderStore = (set, get): OrderStore => ({
  orders: [],
  selectedStoreId: null,
  pendingOrdersCount: 0,
  activityCount: 0,
  isLoading: false,

  setSelectedStoreId: (storeId: string | null) => {
    set(() => ({ selectedStoreId: storeId }));
  },

  fetchOrders: async (storeId: string) => {
    try {
      set(() => ({ isLoading: true }));
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .eq("store_id", storeId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching orders:", error);
        toast.error("Failed to fetch orders. Please try again.");
        set(() => ({ isLoading: false }));
        return;
      }

      // Updated function to fetch order items with menu item details
      const fetchOrderItems = async (orderId: string) => {
        const { data: itemsData, error: itemsError } = await supabase
          .from("order_items")
          .select(
            `
            *,
            menu_items (
              image_url
            )
          `
          )
          .eq("order_id", orderId);

        if (itemsError) {
          console.error("Error fetching order items:", itemsError);
          return [];
        }

        return (
          itemsData?.map((item) => ({
            ...item,
            imageUrl: item.menu_items?.image_url, // Add the image URL from the joined table
          })) || []
        );
      };

      // Fetch order items for each order
      const ordersWithItems = await Promise.all(
        data.map(async (order) => {
          const items = await fetchOrderItems(order.id);
          return { ...order, items };
        })
      );

      // Update the state with the fetched orders
      set(() => ({
        orders: ordersWithItems.map((order) => ({
          id: order.id,
          store_id: order.store_id,
          table_id: order.table_id,
          tableNumber: order.table_number,
          tableName: order.table_name,
          status: order.status as OrderStatus,
          totalAmount: order.total_amount,
          items: order.items,
          specialInstructions: order.special_instructions,
          createdAt: order.created_at,
          updatedAt: order.updated_at,
          is_payed: order.is_payed,
          payment_method: order.payment_method,
        })),
        isLoading: false,
      }));
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders. Please try again.");
      set(() => ({ isLoading: false }));
    }
  },

  updateOrderStatus: async (orderId: string, status: OrderStatus) => {
    try {
      // Get the current order to check previous status
      const currentOrder = get().orders.find(
        (order: Order) => order.id === orderId
      );
      const previousStatus = currentOrder?.status;

      const { error } = await supabase
        .from("orders")
        .update({ status })
        .eq("id", orderId);

      if (error) {
        console.error("Error updating order status:", error);
        toast.error("Failed to update order status. Please try again.");
        return;
      }

      // Handle manual inventory deduction/addition logic
      if (status === "preparing" && previousStatus !== "preparing") {
        // Deduct manual inventory when marking as preparing
        const inventoryResult = await deductManualInventoryForOrder(orderId);
        if (!inventoryResult.success) {
          console.error(
            "Error deducting manual inventory:",
            inventoryResult.error
          );
          // Continue with status update even if inventory fails
        }
      } else if (
        status === "cancelled" &&
        (previousStatus === "preparing" || previousStatus === "ready")
      ) {
        // Add back manual inventory when cancelling a prepared order
        const inventoryResult = await addBackManualInventoryForOrder(orderId);
        if (!inventoryResult.success) {
          console.error(
            "Error adding back manual inventory:",
            inventoryResult.error
          );
          // Continue with status update even if inventory fails
        }
      }

      const updatedOrder = { ...currentOrder, status };
      
      set((state: OrderStore) => ({
        orders: state.orders.map((order) =>
          order.id === orderId ? updatedOrder : order
        ),
      }));

      // Note: Notifications are now handled by the new orderNotification.service.ts
      // which is called directly from the components using updateOrderStatusService

      toast.success("Order status updated successfully!");
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status. Please try again.");
    }
  },

  cancelOrder: async (orderId: string, reason: string) => {
    try {
      // Get the current order to check if it was prepared
      const currentOrder = get().orders.find(
        (order: Order) => order.id === orderId
      );
      const wasEverPrepared =
        currentOrder?.status === "preparing" ||
        currentOrder?.status === "ready";

      const { error } = await supabase
        .from("orders")
        .update({ 
          status: "cancelled", 
          cancel_reason: reason 
        })
        .eq("id", orderId);

      if (error) {
        console.error("Error cancelling order:", error);
        toast.error("Failed to cancel order. Please try again.");
        return;
      }

      // Add back manual inventory if the order was ever prepared
      if (wasEverPrepared) {
        const inventoryResult = await addBackManualInventoryForOrder(orderId);
        if (!inventoryResult.success) {
          console.error(
            "Error adding back manual inventory:",
            inventoryResult.error
          );
          // Continue with cancellation even if inventory fails
        }
      }

      const cancelledOrder = { ...currentOrder, status: "cancelled" };
      
      set((state: OrderStore) => ({
        orders: state.orders.map((order) =>
          order.id === orderId ? cancelledOrder : order
        ),
      }));

      // Note: Notifications are now handled by the new orderNotification.service.ts
      // which is called directly from the components using cancelOrder service

      toast.success("Order cancelled successfully!");
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error("Failed to cancel order. Please try again.");
    }
  },

  unsubscribeFromOrdersRealtime: () => {
    // Remove the listener when the component unmounts
    supabase.removeAllChannels();
  },

  subscribeToOrdersRealtime: (storeId: string) => {
    console.log("🔔 Setting up realtime subscription for store:", storeId);
    
    if (!storeId) {
      console.error("❌ No store ID provided for order subscription");
      return;
    }

    // Unsubscribe from any existing subscription
    supabase.removeAllChannels();

    const channel = supabase
      .channel(`store_orders_${storeId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "orders",
          filter: `store_id=eq.${storeId}`,
        },
        async (payload) => {
          console.log("📡 Realtime event received:", payload.eventType, payload);
          
          if (payload.new) {
            const payloadData = payload.new as Order;
            console.log("📦 Processing order data:", payloadData);

            // Fetch order items with menu item details using join
            const { data: items } = await supabase
              .from("order_items")
              .select(
                `
                *,
                menu_items (
                  image_url
                )
              `
              )
              .eq("order_id", payloadData.id);

            console.log("🛒 Fetched order items:", items);

            const newOrder = {
              id: payloadData.id || "",
              store_id: payloadData.store_id || "",
              table_id: payloadData.table_id || "",
              tableNumber: payloadData.table_number || 0,
              tableName: payloadData.table_name || "",
              status: (payloadData.status as OrderStatus) || "pending",
              totalAmount: payloadData.total_amount || 0,
              items:
                items?.map((item) => ({
                  id: item.id,
                  order_id: item.order_id,
                  menu_item_id: item.menu_item_id,
                  menu_item_name: item.menu_item_name,
                  price: item.price,
                  quantity: item.quantity,
                  notes: item.notes,
                  created_at: item.created_at,
                  updated_at: item.updated_at,
                  imageUrl: item.menu_items?.image_url, // Get image_url from joined menu_items
                })) || [],
              specialInstructions: payloadData.specialInstructions || "",
              createdAt: payloadData.createdAt || new Date().toISOString(),
              updatedAt: payloadData.updatedAt || new Date().toISOString(),
              is_payed: payloadData.is_payed || false,
              payment_method: payloadData.payment_method,
            };

            console.log("✅ Formatted order:", newOrder);

            set((state: OrderStore) => {
              const existingOrderIndex = state.orders.findIndex(
                (order) => order.id === newOrder.id
              );

              if (payload.eventType === "INSERT") {
                console.log("🆕 New order INSERT detected");
                // Note: Notifications are now handled by the new orderNotification.service.ts
                // which is called directly from the submitOrder function
                return { orders: [newOrder, ...state.orders] };
              } else if (payload.eventType === "UPDATE") {
                console.log("🔄 Order UPDATE detected");
                // Note: Notifications are now handled by the new orderNotification.service.ts
                // which is called directly from the components using updateOrderStatusService
                const updatedOrders = [...state.orders];
                if (existingOrderIndex !== -1) {
                  updatedOrders[existingOrderIndex] = newOrder;
                }
                return { orders: updatedOrders };
              } else if (payload.eventType === "DELETE") {
                console.log("🗑️ Order DELETE detected");
                // Remove order
                return {
                  orders: state.orders.filter(
                    (order) => order.id !== newOrder.id
                  ),
                };
              }

              return state;
            });
          }
        }
      )
      .subscribe((status) => {
        console.log("🔌 Realtime subscription status:", status);
      });
  },

  deleteOrder: async (orderId: string) => {
    try {
      const { selectedStoreId } = get();

      if (!selectedStoreId) {
        throw new Error("No store selected");
      }

      // First, mark the order as cancelled in our local state (optimistic update)
      set((state: OrderStore) => ({
        orders: state.orders.filter((order) => order.id !== orderId),
      }));

      // Then, delete the order from the database
      const { error } = await supabase
        .from("orders")
        .delete()
        .eq("id", orderId);

      if (error) {
        console.error("Error deleting order:", error);

        // If there was an error, we need to revert our optimistic update
        get().fetchOrders(selectedStoreId);
        throw error;
      }

      // Show success toast
      toast.success("Order deleted successfully");
    } catch (error) {
      console.error("Error in deleteOrder:", error);
      toast.error("Failed to delete order. Please try again.");

      // Re-fetch orders to ensure UI is in sync with the backend
      const { selectedStoreId } = get();
      if (selectedStoreId) {
        get().fetchOrders(selectedStoreId);
      }
    }
  },

  // Implement missing methods
  updatePendingOrdersCount: () => {
    const pendingOrders = get().orders.filter(
      (order) => order.status === "pending" || order.status === "preparing"
    ).length;

    set(() => ({ pendingOrdersCount: pendingOrders }));
  },

  markActivityViewed: async (activityId: string) => {
    try {
      // Update activity as viewed in database
      const { error } = await supabase
        .from("activity_logs")
        .update({ details: { viewed: true } })
        .eq("id", activityId);

      if (error) {
        console.error("Error marking activity as viewed:", error);
        return;
      }

      // Decrement activity count if greater than 0
      set((state: OrderStore) => ({
        activityCount: Math.max(0, state.activityCount - 1),
      }));
    } catch (error) {
      console.error("Error marking activity as viewed:", error);
    }
  },

  getTotalOrderAmount: () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return get()
      .orders.filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= today && order.status !== "cancelled";
      })
      .reduce((sum, order) => sum + order.totalAmount, 0);
  },

  subscribeToActivitiesRealtime: (storeId: string) => {
    if (!storeId) {
      console.error(
        "No store ID provided for activities realtime subscription"
      );
      return;
    }

    const channel = supabase
      .channel("activity_logs")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "activity_logs",
          filter: `store_id=eq.${storeId}`,
        },
        (payload) => {

          const activity = payload.new as ActivityLog;

          // Check if the activity is unviewed and relevant
          if (
            activity &&
            [
              "order_reminder",
              "payment_request",
              "assistance_request",
            ].includes(activity.entity_type)
          ) {
            const isUnviewed = !activity.details?.viewed;

            if (isUnviewed) {
              set((state: OrderStore) => ({
                activityCount: state.activityCount + 1,
              }));
            }
          }
        }
      )
      .subscribe();
  },

  unsubscribeFromActivitiesRealtime: () => {
    // This is handled by the removeAllChannels in unsubscribeFromOrdersRealtime
  },

  addOrder: (order: Order) => {
    set((state: OrderStore) => ({
      orders: [order, ...state.orders],
    }));
  },

  initGlobalSubscription: () => {
    const storeId = get().selectedStoreId;
    if (storeId) {
      get().subscribeToOrdersRealtime(storeId);
      get().subscribeToActivitiesRealtime(storeId);
    } else {
      console.warn("Cannot initialize global subscription: No store selected");
    }
  },

  // Add these methods to the store
  getAllEmployeeMetrics: () => {
    const metrics: Record<string, EmployeeMetrics> = {};

    get().orders.forEach((order) => {
      if (order.status === "delivered" || order.status === "completed") {
        const employeeId = order.assigned_to;
        if (employeeId) {
          if (!metrics[employeeId]) {
            metrics[employeeId] = {
              completedOrders: 0,
              totalRevenue: 0,
              employeeId,
            };
          }
          metrics[employeeId].completedOrders += 1;
          metrics[employeeId].totalRevenue += order.totalAmount;
        }
      }
    });

    return Object.values(metrics);
  },

  getEmployeeMetrics: (employeeId: string) => {
    return (
      get()
        .getAllEmployeeMetrics()
        .find((m) => m.employeeId === employeeId) || {
        completedOrders: 0,
        totalRevenue: 0,
        employeeId,
      }
    );
  },

  updateOrder: (updatedOrder) => {
    set((state) => ({
      orders: state.orders.map((order) =>
        order.id === updatedOrder.id ? { ...order, ...updatedOrder } : order
      ),
    }));
  },
});

export const useOrderStore = create<OrderStore>(createOrderStore);
// Export the Order and OrderStatus types from types/orders correctly for TypeScript with isolatedModules
export type { Order, OrderStatus } from "@/types/orders";
